import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { toast } from "sonner";
import { Activity, Layers, AlertTriangle, CheckCircle2, DollarSign, KeyRound, RotateCw, Ban, Copy, ShieldAlert } from "lucide-react";

function OperatorTokenPanel() {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [fresh, setFresh] = useState(null);
  const loadSt = useCallback(async () => {
    try { const r = await api.get("/admin/operator-token/status"); setSt(r.data); } catch { /* non-admin */ }
  }, []);
  useEffect(() => { loadSt(); }, [loadSt]);

  const rotate = async () => {
    if (!window.confirm("Rotate the CLAUDE OPERATOR token? The current token stops working immediately.")) return;
    setBusy(true);
    try { const r = await api.post("/admin/operator-token/rotate"); setFresh(r.data.token); toast.success("Operator token rotated — copy it now, shown once."); await loadSt(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Rotate failed"); } finally { setBusy(false); }
  };
  const revoke = async () => {
    if (!window.confirm("Revoke (kill) the operator token now? It becomes fully dead until rotated.")) return;
    setBusy(true);
    try { await api.post("/admin/operator-token/revoke"); setFresh(null); toast.success("Operator token revoked."); await loadSt(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Revoke failed"); } finally { setBusy(false); }
  };
  if (!st) return null;
  return (
    <div className="bg-white border border-zinc-200 rounded-sm mt-6" data-testid="admin-operator-token-panel">
      <div className="px-4 py-3 border-b border-zinc-200 flex items-center gap-2">
        <KeyRound className="h-3.5 w-3.5 text-zinc-500" />
        <span className="text-[10px] tracking-[0.1em] font-semibold text-zinc-500">CLAUDE OPERATOR TOKEN</span>
        <span className={`ml-auto text-[10px] font-mono px-2 py-0.5 rounded-full ${st.enabled ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}
          data-testid="operator-token-state">{st.enabled ? "ENABLED" : "REVOKED"}</span>
      </div>
      <div className="p-4 text-sm">
        <div className="grid grid-cols-2 gap-y-1 text-xs text-zinc-600">
          <span className="text-zinc-400">Identity</span><span className="font-medium">{st.identity}</span>
          <span className="text-zinc-400">Created</span><span>{st.created_at ? new Date(st.created_at).toLocaleString() : "—"}</span>
          <span className="text-zinc-400">Last used</span><span>{st.last_used_at ? new Date(st.last_used_at).toLocaleString() : "never"}</span>
        </div>
        {fresh && (
          <div className="mt-3 p-2 bg-amber-50 border border-amber-200 rounded-sm">
            <div className="text-[10px] font-semibold text-amber-700 mb-1">NEW TOKEN — shown once, store as a secret</div>
            <div className="flex items-center gap-2">
              <code className="text-[11px] break-all flex-1 text-zinc-800" data-testid="operator-token-fresh">{fresh}</code>
              <button onClick={() => { navigator.clipboard.writeText(fresh); toast.success("Copied"); }}
                className="shrink-0 p-1 hover:bg-amber-100 rounded" data-testid="operator-token-copy"><Copy className="h-3.5 w-3.5 text-amber-700" /></button>
            </div>
          </div>
        )}
        <div className="flex gap-2 mt-4">
          <button onClick={rotate} disabled={busy} data-testid="operator-token-rotate-btn"
            className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 bg-zinc-900 text-white rounded-sm hover:bg-zinc-700 disabled:opacity-50">
            <RotateCw className="h-3.5 w-3.5" /> Rotate
          </button>
          <button onClick={revoke} disabled={busy || !st.enabled} data-testid="operator-token-revoke-btn"
            className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 border border-red-300 text-red-700 rounded-sm hover:bg-red-50 disabled:opacity-40">
            <Ban className="h-3.5 w-3.5" /> Revoke
          </button>
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, icon: Icon, tone = "zinc", testid }) {
  const tones = { zinc: "text-zinc-900", blue: "text-blue-600", amber: "text-amber-600", emerald: "text-emerald-600" };
  return (
    <div data-testid={testid} className="bg-white border border-zinc-200 rounded-sm p-5">
      <div className="flex items-center justify-between">
        <span className="text-[10px] tracking-[0.1em] font-semibold text-zinc-500">{label}</span>
        <Icon className={`h-4 w-4 ${tones[tone]}`} strokeWidth={1.75} />
      </div>
      <div className={`mt-3 text-2xl font-display font-bold tnum ${tones[tone]}`}>{value}</div>
    </div>
  );
}

function GroundingSweepPanel() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pulse, setPulse] = useState(false);
  const navigate = useNavigate();
  const loc = useLocation();
  const panelRef = useRef(null);
  const load = useCallback(async () => {
    try { const r = await api.get("/pricing/line-grounding/sweep/latest"); setD(r.data); } catch { /* non-admin */ }
  }, []);
  useEffect(() => { load(); }, [load]);
  // Opened via the top-nav grounding-health badge (#grounding-sweep): scroll into view + briefly highlight.
  useEffect(() => {
    if (loc.hash !== "#grounding-sweep") return;
    let t1, t2;
    const doScroll = () => {
      if (!panelRef.current) return;
      panelRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      setPulse(true);
      t2 = setTimeout(() => setPulse(false), 2200);
    };
    // Admin loads KPIs/tables async (page height shifts after mount) — settle layout, then scroll (+ retry).
    const raf = requestAnimationFrame(doScroll);
    t1 = setTimeout(doScroll, 650);
    return () => { cancelAnimationFrame(raf); clearTimeout(t1); clearTimeout(t2); };
  }, [loc.hash, d]);
  const run = async () => {
    setBusy(true);
    const tid = toast.loading("Running grounding retro-sweep across all active bids…");
    try {
      const r = await api.post("/pricing/line-grounding/retro-check");
      toast.success(`Sweep complete — ${r.data.with_findings}/${r.data.checked} bid(s) with findings.`, { id: tid });
      setD(r.data);
      window.dispatchEvent(new CustomEvent("ironclad:grounding-sweep-updated", { detail: { mq: r.data.missing_quantity_bids || 0 } }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sweep failed.", { id: tid });
    } finally { setBusy(false); }
  };
  const mq = (d?.reports || []).filter((r) => (r.missing_quantity || []).length);
  return (
    <div ref={panelRef} id="grounding-sweep" data-testid="grounding-sweep-panel"
      className={`bg-white border rounded-sm mt-6 scroll-mt-6 transition-shadow ${pulse ? "border-amber-400 ring-2 ring-amber-400" : "border-zinc-200"}`}>
      <div className="px-4 py-3 border-b border-zinc-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ShieldAlert className="h-4 w-4 text-amber-600" />
          <span className="text-[10px] tracking-[0.1em] font-semibold text-zinc-500">GROUNDING RETRO-SWEEP · NIGHTLY</span>
        </div>
        <button onClick={run} disabled={busy} data-testid="grounding-sweep-run-btn"
          className="inline-flex items-center gap-1.5 text-[11px] font-semibold bg-zinc-900 text-white px-2.5 py-1 rounded hover:bg-zinc-800 disabled:opacity-50">
          <RotateCw className={`h-3 w-3 ${busy ? "animate-spin" : ""}`} /> Run sweep now
        </button>
      </div>
      <div className="p-4">
        {(!d || !d.taken_at) ? (
          <p className="text-sm text-zinc-400" data-testid="grounding-sweep-empty">
            No sweep has run yet — it runs automatically each night, or click “Run sweep now”.
          </p>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm">
              <span className="text-zinc-500">Last run{" "}
                <span className="text-zinc-800 font-medium">{new Date(d.taken_at).toLocaleString()}</span>{" "}
                <span className="text-zinc-400">({d.trigger})</span></span>
              <span className="text-zinc-500">Checked{" "}
                <span className="tnum font-semibold text-zinc-800" data-testid="grounding-sweep-checked">{d.checked}</span></span>
              <span className="text-zinc-500">With findings{" "}
                <span className="tnum font-semibold text-zinc-800" data-testid="grounding-sweep-findings">{d.with_findings}</span></span>
              <span className={`font-semibold ${d.missing_quantity_bids ? "text-amber-700" : "text-emerald-600"}`}
                data-testid="grounding-sweep-mq-count">{d.missing_quantity_bids || 0} missing-quantity</span>
            </div>
            {mq.length > 0 ? (
              <div className="mt-3 rounded border border-amber-200 bg-amber-50 divide-y divide-amber-100" data-testid="grounding-sweep-mq-list">
                {mq.map((r) => (
                  <div key={r.bid_id} className="p-3">
                    <button onClick={() => navigate(`/bids/${r.bid_id}`)}
                      className="text-sm font-semibold text-amber-900 hover:underline text-left"
                      data-testid={`grounding-sweep-bid-${r.bid_id}`}>{r.title || r.bid_id}</button>
                    <ul className="mt-1 space-y-0.5">
                      {(r.missing_quantity || []).map((m, i) => (
                        <li key={`${r.bid_id}-${i}`} className="text-[12px] text-amber-800 leading-snug">• {m}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            ) : (
              <p className="mt-2 text-[13px] text-emerald-600" data-testid="grounding-sweep-clean">
                No missing-quantity fabrications — no per-capacity line is riding a blank system size.
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [runs, setRuns] = useState([]);
  const [audit, setAudit] = useState([]);
  const navigate = useNavigate();
  const ref = useRef(null);

  const load = useCallback(async () => {
    const [s, r, a] = await Promise.all([api.get("/admin/stats"), api.get("/admin/runs"), api.get("/admin/audit")]);
    setStats(s.data); setRuns(r.data); setAudit(a.data);
  }, []);

  useEffect(() => {
    load();
    ref.current = setInterval(load, 2500);
    return () => clearInterval(ref.current);
  }, [load]);

  return (
    <div className="p-8 max-w-7xl">
      <div className="flex items-center gap-2">
        <Activity className="h-5 w-5 text-blue-600" />
        <h1 className="font-display font-bold text-2xl tracking-tight text-zinc-900">Monitoring Dashboard</h1>
        <span className="ml-2 text-[10px] tracking-wide text-emerald-600 font-mono flex items-center gap-1">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" /> live
        </span>
      </div>
      <p className="text-sm text-zinc-500 mt-1">Every run across the team, with the full audit trail.</p>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mt-8">
        <Kpi label="Total Bids" value={stats?.total_bids ?? "—"} icon={Layers} testid="admin-kpi-total" />
        <Kpi label="Running" value={stats?.running ?? 0} icon={Activity} tone="blue" testid="admin-kpi-running" />
        <Kpi label="Awaiting Approval" value={stats?.awaiting_approval ?? 0} icon={AlertTriangle} tone="amber" testid="admin-kpi-awaiting" />
        <Kpi label="Submitted" value={stats?.submitted ?? 0} icon={CheckCircle2} tone="emerald" testid="admin-kpi-submitted" />
        <Kpi label="Pipeline Value" value={stats?.pipeline_value ?? "—"} icon={DollarSign} testid="admin-kpi-value" />
      </div>

      <div className="grid grid-cols-12 gap-6 mt-8">
        <div className="col-span-12 lg:col-span-7">
          <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-zinc-200 text-[10px] tracking-[0.1em] font-semibold text-zinc-500">All Runs</div>
            <table className="w-full text-sm" data-testid="admin-runs-table">
              <thead><tr className="border-b border-zinc-200 text-zinc-500 text-xs">
                <th className="text-left font-medium px-4 py-2">Solicitation</th>
                <th className="text-left font-medium px-4 py-2">Owner</th>
                <th className="text-left font-medium px-4 py-2">Stage</th>
                <th className="text-left font-medium px-4 py-2">Status</th>
              </tr></thead>
              <tbody>
                {runs.map((b) => (
                  <tr key={b.id} onClick={() => navigate(`/bids/${b.id}`)}
                    className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50 cursor-pointer transition-colors">
                    <td className="px-4 py-2">
                      <div className="font-medium text-zinc-900">{b.solicitation_number}</div>
                      <div className="text-xs text-zinc-500 truncate max-w-[200px]">{b.title}</div>
                    </td>
                    <td className="px-4 py-2 text-zinc-600">{b.owner_name}</td>
                    <td className="px-4 py-2 font-mono text-xs text-zinc-500">{b.current_stage > 0 ? `${b.current_stage}/12` : "—"}</td>
                    <td className="px-4 py-2"><StatusBadge status={b.status} /></td>
                  </tr>
                ))}
                {runs.length === 0 && <tr><td colSpan={4} className="px-4 py-10 text-center text-zinc-400">No runs yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>

        <div className="col-span-12 lg:col-span-5">
          <div className="bg-white border border-zinc-200 rounded-sm">
            <div className="px-4 py-3 border-b border-zinc-200 text-[10px] tracking-[0.1em] font-semibold text-zinc-500">Global Audit Trail</div>
            <div className="max-h-[420px] overflow-auto divide-y divide-zinc-100" data-testid="admin-audit-trail">
              {audit.map((e, i) => (
                <div key={e.id || `audit-${i}`} className="px-4 py-2 flex items-start gap-2.5 text-sm">
                  <span className={`mt-1.5 h-1.5 w-1.5 rounded-full shrink-0 ${
                    e.level === "success" ? "bg-emerald-500" : e.level === "warning" ? "bg-amber-500" :
                    e.level === "error" ? "bg-red-500" : "bg-zinc-300"}`} />
                  <div className="flex-1 min-w-0">
                    <span className="font-medium text-zinc-800">{e.action}</span>
                    {e.detail && <div className="text-xs text-zinc-500 leading-snug truncate">{e.detail}</div>}
                  </div>
                  <span className="font-mono text-[10px] text-zinc-400 shrink-0">{new Date(e.created_at).toLocaleTimeString()}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <OperatorTokenPanel />
      <GroundingSweepPanel />
    </div>
  );
}
